import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ReadingProgress } from "../components/ReadingProgress";
import { SocialShare } from "../components/SocialShare";
import { ArticleCard } from "../components/ArticleCard";
import { CATEGORY_LABELS } from "../lib/constants";

export default function ArticlePage() {
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);
        setNotFound(false);
        api.get(`/articles/${id}`)
            .then((r) => setArticle(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="max-w-2xl mx-auto px-4 py-24 text-center"><p className="font-mono text-sm text-muted-foreground animate-pulse">Loading article…</p></div>;
    }
    if (notFound || !article) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="article-not-found">
                <h1 className="font-serif text-4xl font-bold">Article not found</h1>
                <Link to="/" className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-accent">← Back home</Link>
            </div>
        );
    }

    const published = article.published_at ? new Date(article.published_at) : null;

    return (
        <>
            <ReadingProgress />
            <article className="py-10" data-testid="article-page">
                <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="max-w-3xl mx-auto text-center">
                        <Link to={`/category/${article.category}`} className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-accent">
                            {CATEGORY_LABELS[article.category] || article.category}
                        </Link>
                        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.08] mt-4" data-testid="article-title">
                            {article.title}
                        </h1>
                        {article.excerpt && <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{article.excerpt}</p>}
                        <div className="mt-6 flex items-center justify-center gap-3 font-mono text-xs text-muted-foreground border-y border-hair py-3">
                            {article.author && <span>{article.author}</span>}
                            {published && <span>·</span>}
                            {published && <time>{published.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time>}
                            {article.wordcount > 0 && <><span>·</span><span>{Math.max(1, Math.round(article.wordcount / 200))} min read</span></>}
                        </div>
                    </div>

                    {/* Hero image */}
                    {article.thumbnail && (
                        <div className="max-w-4xl mx-auto mt-8">
                            <img src={article.thumbnail} alt={article.title} className="w-full aspect-video object-cover bg-muted" />
                        </div>
                    )}

                    {/* Body + share rail */}
                    <div className="max-w-3xl mx-auto mt-10 relative">
                        <div className="hidden lg:block absolute -left-20 top-0 sticky-share">
                            <div className="sticky top-28">
                                <SocialShare title={article.title} orientation="vertical" />
                            </div>
                        </div>

                        <div className="lg:hidden mb-8 flex items-center gap-3">
                            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Share</span>
                            <SocialShare title={article.title} orientation="horizontal" />
                        </div>

                        <div
                            className="article-body font-sans text-ink"
                            data-testid="article-body"
                            dangerouslySetInnerHTML={{ __html: article.body }}
                        />

                        <div className="mt-10 pt-6 border-t border-hair">
                            <a href={article.url} target="_blank" rel="noopener noreferrer" className="font-mono text-xs uppercase tracking-wider text-accent link-underline">
                                Read the original on The Guardian ↗
                            </a>
                        </div>
                    </div>
                </div>

                {/* Related */}
                {article.related?.length > 0 && (
                    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-20">
                        <div className="border-b-2 border-ink pb-2 mb-8">
                            <h2 className="font-serif text-2xl font-bold">More in {CATEGORY_LABELS[article.category] || article.category}</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
                            {article.related.map((a) => (
                                <ArticleCard key={a.id} article={a} />
                            ))}
                        </div>
                    </div>
                )}
            </article>
        </>
    );
}
